const LOAD_EVENT_NAMES = ['DOMContentLoaded', 'turbolinks:load', 'turbo:load'];

const SELECTORS = {
  root: '[data-site-costs-beta-page="true"]',
  siteToggleRow: '.js-beta-site-toggle-row',
  contractRows: '.js-beta-contract-row',
  sortableHeader: '.js-sortable-header',
  formulaTooltip: '.js-site-costs-beta-tooltip',
  commentButton: '[data-site-cost-comment-button="true"]',
  commentsPanel: '[data-site-cost-comments-panel]',
  commentsPanelHeader: '[data-site-cost-comments-panel-header]',
  commentsClose: '[data-site-cost-comments-close]',
  commentsError: '[data-site-cost-comments-error]',
  commentsLoading: '[data-site-cost-comments-loading]',
  commentsList: '[data-site-cost-comments-list]',
  commentsNewForm: '[data-site-cost-comments-new-form]',
  commentsNewTextarea: '[data-site-cost-comments-new-textarea]',
  commentsCreateButton: '[data-site-cost-comments-create]',
  commentsCustomerName: '[data-site-cost-comments-customer-name]',
  commentsTargetCodeLabel: '[data-site-cost-comments-target-code-label]',
  commentsTargetCode: '[data-site-cost-comments-target-code]',
  commentsTargetName: '[data-site-cost-comments-target-name]',
  commentsSelectedMonth: '[data-site-cost-comments-selected-month]',
  historyModal: '[data-site-cost-comments-history-modal]',
  historyLoading: '[data-site-cost-comments-history-loading]',
  historyList: '[data-site-cost-comments-history-list]'
};

const STOP_TOGGLE_SELECTORS = 'a, button, input, select, textarea, label, .button';
const DRAG_HANDLE_IGNORE_SELECTORS = 'a, button, input, select, textarea, label, .button';
const PANEL_GAP = 12;
const PANEL_MARGIN = 16;
const PANEL_HEADER_HEIGHT = 60;
const PANEL_WIDTH = 420;
const PANEL_MAX_HEIGHT = 520;
const PANEL_MIN_HEIGHT = 180;

const query = (selector, root = document) => root.querySelector(selector);
const queryAll = (selector, root = document) => Array.from(root.querySelectorAll(selector));
const getJQuery = () => window.jQuery || window.$;

let activePage = null;
let lifecycleListenersInstalled = false;

const requestFrame = (callback) => {
  if (window.requestAnimationFrame) return window.requestAnimationFrame(callback);
  return window.setTimeout(callback, 16);
};

const cancelFrame = (frameId) => {
  if (window.cancelAnimationFrame) {
    window.cancelAnimationFrame(frameId);
    return;
  }

  window.clearTimeout(frameId);
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function readNumber(value, fallback) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function defaultPanelHeight(viewportHeight) {
  return Math.min(viewportHeight * 0.5, PANEL_MAX_HEIGHT);
}

export function clampPanelPosition(position, viewportWidth, viewportHeight, headerHeight = PANEL_HEADER_HEIGHT, margin = PANEL_MARGIN) {
  const width = Math.min(readNumber(position.width, PANEL_WIDTH), Math.max(viewportWidth - margin * 2, 0));
  const heightLimit = Math.max(viewportHeight - headerHeight - margin * 2, 0);
  const height = Math.min(readNumber(position.height, defaultPanelHeight(viewportHeight)), heightLimit);
  const minTop = headerHeight + margin;
  const maxTop = Math.max(minTop, viewportHeight - margin - height);
  const minLeft = margin;
  const maxLeft = Math.max(minLeft, viewportWidth - margin - width);

  return {
    top: clamp(readNumber(position.top, minTop), minTop, maxTop),
    left: clamp(readNumber(position.left, maxLeft), minLeft, maxLeft),
    width,
    height
  };
}

export function calculateCommentsPanelPlacement({
  targetRect,
  currentPosition,
  viewportWidth,
  viewportHeight,
  headerHeight = PANEL_HEADER_HEIGHT,
  margin = PANEL_MARGIN,
  gap = PANEL_GAP,
  panelWidth = PANEL_WIDTH
}) {
  const visibleTop = headerHeight;
  const visibleBottom = viewportHeight;
  const panelMaxHeight = defaultPanelHeight(viewportHeight);
  const width = Math.min(panelWidth, Math.max(viewportWidth - margin * 2, 0));
  const topLimit = headerHeight + margin;
  const bottomLimit = viewportHeight - margin;
  const isVisible = targetRect && targetRect.bottom > visibleTop && targetRect.top < visibleBottom;

  if (!isVisible) {
    return clampPanelPosition(currentPosition || {
      top: topLimit,
      left: viewportWidth - margin - width,
      width,
      height: panelMaxHeight
    }, viewportWidth, viewportHeight, headerHeight, margin);
  }

  const availableAbove = Math.max(0, targetRect.top - gap - topLimit);
  const availableBelow = Math.max(0, bottomLimit - (targetRect.bottom + gap));
  const placeBelow = availableBelow >= availableAbove;
  const availableSpace = placeBelow ? availableBelow : availableAbove;
  const height = Math.min(panelMaxHeight, Math.max(availableSpace, PANEL_MIN_HEIGHT));
  const top = placeBelow
    ? clamp(targetRect.bottom + gap, topLimit, Math.max(topLimit, bottomLimit - height))
    : clamp(targetRect.top - gap - height, topLimit, Math.max(topLimit, bottomLimit - height));

  return clampPanelPosition({
    top,
    left: viewportWidth - margin - width,
    width,
    height
  }, viewportWidth, viewportHeight, headerHeight, margin);
}

function setInert(element, inert) {
  if (!element) return;

  if (inert) {
    element.setAttribute('inert', '');
  } else {
    element.removeAttribute('inert');
  }

  if ('inert' in element) element.inert = inert;
}

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : null;
}

async function parseResponseBody(response) {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (_error) {
      return null;
    }
  }

  return null;
}

function extractErrors(body, fallbackMessage) {
  if (body && Array.isArray(body.errors) && body.errors.length > 0) return body.errors;
  if (body && typeof body.error === 'string' && body.error) return [body.error];
  return [fallbackMessage];
}

async function requestJson(url, { method = 'GET', body } = {}) {
  const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
  if (method !== 'GET') {
    const token = csrfToken();
    if (!token) throw new Error('missing csrf-token');
    headers['Content-Type'] = 'application/json';
    headers['X-CSRF-Token'] = token;
  }

  const response = await fetch(url, {
    method,
    headers,
    credentials: 'same-origin',
    body: body ? JSON.stringify(body) : undefined
  });

  const responseBody = await parseResponseBody(response);
  if (!response.ok || response.redirected || !responseBody || typeof responseBody !== 'object') {
    const error = new Error(extractErrors(responseBody, `通信に失敗しました（HTTP ${response.status}）`).join('\n'));
    error.responseBody = responseBody;
    throw error;
  }

  return responseBody;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function buildSortUrl(currentUrl, { key, currentSort, currentDir }) {
  const nextUrl = new URL(currentUrl, window.location.origin);
  const nextDir = key === currentSort && currentDir !== 'desc' ? 'desc' : 'asc';

  nextUrl.searchParams.set('sort', key);
  nextUrl.searchParams.set('sort_dir', nextDir);
  nextUrl.searchParams.delete('page');

  return nextUrl.toString();
}

export function shouldShowNewCommentForm(comments, selectedTargetMonth) {
  return !comments.some((comment) => comment.target_month === selectedTargetMonth);
}

export function historyCommentText(history) {
  return String(history.comment || '').trim() || '-';
}

export function buildHistoryTableHtml(histories) {
  if (!Array.isArray(histories) || histories.length === 0) {
    return '<p class="beta-comments-history__empty">履歴はありません</p>';
  }

  const rows = histories.map((history) => `
    <tr>
      <td>${escapeHtml(history.operated_at_label)}</td>
      <td>${escapeHtml(history.action_type)}</td>
      <td>${escapeHtml(history.operator_name)}</td>
      <td class="beta-comments-history__changes">${escapeHtml(historyCommentText(history))}</td>
    </tr>
  `).join('');

  return `
    <table class="beta-comments-history__table">
      <thead>
        <tr>
          <th>変更日時</th>
          <th>操作</th>
          <th>変更者</th>
          <th>変更内容</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
}

function initializeFormulaTooltips(root) {
  const $ = getJQuery();
  if (!$ || typeof $.fn?.popup !== 'function') return;

  queryAll(SELECTORS.formulaTooltip, root).forEach((element) => {
    if (element.dataset.betaTooltipBound === 'true') return;

    $(element).popup({
      on: 'hover',
      html: true,
      position: 'top center',
      closable: true,
      hoverable: true,
      variation: element.dataset.variation || 'mini',
      content() {
        return element.getAttribute('data-html');
      }
    });

    const showPopup = () => {
      $(element).popup('show');
    };

    const hidePopup = () => {
      $(element).popup('hide');
    };

    const handleClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      showPopup();
    };

    const handleFocus = () => {
      showPopup();
    };

    const handleBlur = () => {
      hidePopup();
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        hidePopup();
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        showPopup();
      }
    };

    element.addEventListener('click', handleClick);
    element.addEventListener('focus', handleFocus);
    element.addEventListener('blur', handleBlur);
    element.addEventListener('keydown', handleKeydown);

    // Store handlers for later cleanup
    element.betaTooltipHandlers = {
      click: handleClick,
      focus: handleFocus,
      blur: handleBlur,
      keydown: handleKeydown
    };
    element.dataset.betaTooltipBound = 'true';
  });
}

function cleanupFormulaTooltips(root) {
  const $ = getJQuery();
  queryAll(SELECTORS.formulaTooltip, root).forEach((element) => {
    if (element.dataset.betaTooltipBound !== 'true') return;

    // Remove event listeners
    if (element.betaTooltipHandlers) {
      element.removeEventListener('click', element.betaTooltipHandlers.click);
      element.removeEventListener('focus', element.betaTooltipHandlers.focus);
      element.removeEventListener('blur', element.betaTooltipHandlers.blur);
      element.removeEventListener('keydown', element.betaTooltipHandlers.keydown);
      delete element.betaTooltipHandlers;
    }

    // Destroy popup if jQuery is available
    if ($ && typeof $.fn?.popup === 'function') {
      try {
        $(element).popup('destroy');
      } catch (_error) {
        // Ignore errors if popup is not initialized
      }
    }

    // Remove bound flag
    delete element.dataset.betaTooltipBound;
  });
}

export class SiteCostsBetaPage {
  constructor(root) {
    this.root = root;
    this.baseCommentsPath = root.dataset.siteCostCommentsPath || '/site_cost_comments';
    this.panel = query(SELECTORS.commentsPanel, root);
    this.panelHeader = query(SELECTORS.commentsPanelHeader, root);
    this.errorBox = query(SELECTORS.commentsError, root);
    this.loading = query(SELECTORS.commentsLoading, root);
    this.list = query(SELECTORS.commentsList, root);
    this.newForm = query(SELECTORS.commentsNewForm, root);
    this.newTextarea = query(SELECTORS.commentsNewTextarea, root);
    this.historyModal = query(SELECTORS.historyModal, root);
    this.historyLoading = query(SELECTORS.historyLoading, root);
    this.historyList = query(SELECTORS.historyList, root);
    this.currentTarget = null;
    this.currentTargetRow = null;
    this.panelPlacementMode = 'auto';
    this.commentRequestId = 0;
    this.historyRequestId = 0;
    this.panelSyncFrame = null;
    this.dragState = null;
    this.isConnected = false;
    this.handleRootClickBound = (event) => this.handleClick(event);
    this.handleKeydownBound = (event) => this.handleKeydown(event);
    this.handleViewportChangeBound = (event) => this.handleViewportChange(event);
    this.handlePanelPointerDownBound = (event) => this.handlePanelPointerDown(event);
    this.handlePanelPointerMoveBound = (event) => this.handlePanelPointerMove(event);
    this.handlePanelPointerUpBound = (event) => this.handlePanelPointerUp(event);
    this.handlePanelPointerCancelBound = (event) => this.handlePanelPointerCancel(event);
  }

  connect() {
    if (this.isConnected) return;

    this.root.dataset.siteCostsBetaPageBound = 'true';
    this.isConnected = true;

    initializeFormulaTooltips(this.root);
    this.initializeHistoryModal();
    this.root.addEventListener('click', this.handleRootClickBound);
    document.addEventListener('keydown', this.handleKeydownBound);
    window.addEventListener('scroll', this.handleViewportChangeBound, true);
    window.addEventListener('resize', this.handleViewportChangeBound);
    if (this.panelHeader) {
      this.panelHeader.addEventListener('pointerdown', this.handlePanelPointerDownBound);
    }
  }

  disconnect() {
    if (!this.isConnected) return;

    this.isConnected = false;
    this.root.removeEventListener('click', this.handleRootClickBound);
    document.removeEventListener('keydown', this.handleKeydownBound);
    window.removeEventListener('scroll', this.handleViewportChangeBound, true);
    window.removeEventListener('resize', this.handleViewportChangeBound);
    if (this.panelHeader) {
      this.panelHeader.removeEventListener('pointerdown', this.handlePanelPointerDownBound);
    }

    this.cancelPanelSync();
    this.stopPanelDrag();
    this.commentRequestId += 1;
    this.historyRequestId += 1;
    this.currentTarget = null;
    this.currentTargetRow = null;

    if (this.panel) {
      this.panel.classList.remove('is-open');
      this.panel.classList.remove('is-dragging');
      this.panel.setAttribute('aria-hidden', 'true');
      setInert(this.panel, true);
    }

    this.hideError();
    if (this.historyLoading) this.historyLoading.hidden = true;
    if (this.historyModal) {
      const $ = getJQuery();
      if ($ && typeof $.fn?.modal === 'function' && this.historyModal.classList.contains('active')) {
        $(this.historyModal).modal('hide');
      }
      delete this.historyModal.dataset.bound;
    }

    // Cleanup formula tooltips
    cleanupFormulaTooltips(this.root);

    delete this.root.dataset.siteCostsBetaPageBound;
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    if (!this.panel || !this.panel.classList.contains('is-open')) return;
    if (this.historyModal && this.historyModal.classList.contains('active')) return;

    this.closePanel();
  }

  initializeHistoryModal() {
    const $ = getJQuery();
    if (!this.historyModal || !$ || typeof $.fn?.modal !== 'function') return;
    if (this.historyModal.dataset.bound === 'true') return;

    $(this.historyModal).modal({ closable: true, observeChanges: true });
    this.historyModal.dataset.bound = 'true';
  }

  handleClick(event) {
    if (event.target.closest(SELECTORS.formulaTooltip)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const closeButton = event.target.closest(SELECTORS.commentsClose);
    if (closeButton) {
      event.preventDefault();
      this.closePanel();
      return;
    }

    const commentButton = event.target.closest(SELECTORS.commentButton);
    if (commentButton) {
      event.preventDefault();
      event.stopPropagation();
      this.openComments(commentButton);
      return;
    }

    const createButton = event.target.closest(SELECTORS.commentsCreateButton);
    if (createButton) {
      event.preventDefault();
      this.createComment(createButton);
      return;
    }

    const editButton = event.target.closest('[data-site-cost-comment-edit]');
    if (editButton) {
      event.preventDefault();
      this.toggleCardEditing(editButton.closest('.beta-comment-card'), true);
      return;
    }

    const cancelButton = event.target.closest('[data-site-cost-comment-cancel]');
    if (cancelButton) {
      event.preventDefault();
      this.toggleCardEditing(cancelButton.closest('.beta-comment-card'), false);
      return;
    }

    const saveButton = event.target.closest('[data-site-cost-comment-save]');
    if (saveButton) {
      event.preventDefault();
      this.updateComment(saveButton.closest('.beta-comment-card'), saveButton);
      return;
    }

    const historyButton = event.target.closest('[data-site-cost-comment-history]');
    if (historyButton) {
      event.preventDefault();
      this.openHistory(historyButton.getAttribute('data-history-path'));
      return;
    }

    const sortableHeader = event.target.closest(SELECTORS.sortableHeader);
    if (sortableHeader) {
      const key = sortableHeader.dataset.sortKey;
      const currentSort = new URL(window.location.href).searchParams.get('sort') || '';
      const currentDir = new URL(window.location.href).searchParams.get('sort_dir') || '';
      window.location.href = buildSortUrl(window.location.href, { key, currentSort, currentDir });
      return;
    }

    const siteToggleRow = event.target.closest(SELECTORS.siteToggleRow);
    if (siteToggleRow) {
      if (event.target.closest(STOP_TOGGLE_SELECTORS)) return;
      this.toggleContractRows(siteToggleRow);
    }
  }

  handleViewportChange(event) {
    if (!this.panel || !this.panel.classList.contains('is-open')) return;
    if (this.panelPlacementMode === 'manual' && event.type === 'scroll') return;

    this.schedulePanelSync();
  }

  handlePanelPointerDown(event) {
    if (!this.panel || !this.panel.classList.contains('is-open')) return;
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    if (event.target.closest(DRAG_HANDLE_IGNORE_SELECTORS)) return;

    event.preventDefault();
    this.cancelPanelSync();
    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startPosition: this.readPanelPlacement()
    };
    this.panelPlacementMode = 'manual';
    this.panel.classList.add('is-dragging');
    window.addEventListener('pointermove', this.handlePanelPointerMoveBound);
    window.addEventListener('pointerup', this.handlePanelPointerUpBound);
    window.addEventListener('pointercancel', this.handlePanelPointerCancelBound);
  }

  handlePanelPointerMove(event) {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
    event.preventDefault();

    const nextPosition = clampPanelPosition({
      top: this.dragState.startPosition.top + (event.clientY - this.dragState.startY),
      left: this.dragState.startPosition.left + (event.clientX - this.dragState.startX),
      width: this.dragState.startPosition.width,
      height: this.dragState.startPosition.height
    }, window.innerWidth, window.innerHeight);

    this.applyPanelPlacement(nextPosition);
  }

  handlePanelPointerUp(event) {
    if (this.dragState && event.pointerId !== this.dragState.pointerId) return;
    this.stopPanelDrag();
  }

  handlePanelPointerCancel(event) {
    if (this.dragState && event.pointerId !== this.dragState.pointerId) return;
    this.stopPanelDrag();
  }

  stopPanelDrag() {
    if (this.panel) {
      this.panel.classList.remove('is-dragging');
    }

    this.dragState = null;
    window.removeEventListener('pointermove', this.handlePanelPointerMoveBound);
    window.removeEventListener('pointerup', this.handlePanelPointerUpBound);
    window.removeEventListener('pointercancel', this.handlePanelPointerCancelBound);
  }

  toggleContractRows(siteRow) {
    const siteId = siteRow.getAttribute('data-site-id');
    const rows = queryAll(`${SELECTORS.contractRows}[data-site-id="${siteId}"]`, this.root);
    if (rows.length === 0) return;

    const willExpand = rows[0].style.display === 'none';
    rows.forEach((row, index) => {
      row.style.display = willExpand ? '' : 'none';
      row.classList.toggle('is-expanded-child', willExpand);
      row.classList.toggle('is-last-expanded-child', willExpand && index === rows.length - 1);
    });
    siteRow.classList.toggle('is-expanded-parent', willExpand);
  }

  setPanelMeta(dataset) {
    query(SELECTORS.commentsCustomerName, this.panel).textContent = dataset.customerName || '';
    query(SELECTORS.commentsTargetCodeLabel, this.panel).textContent = dataset.targetCodeLabel || '';
    query(SELECTORS.commentsTargetCode, this.panel).textContent = dataset.targetCode || '';
    query(SELECTORS.commentsTargetName, this.panel).textContent = dataset.targetName || '';
    query(SELECTORS.commentsSelectedMonth, this.panel).textContent = dataset.selectedTargetMonth || '';
  }

  openPanel() {
    if (!this.panel) return;

    setInert(this.panel, false);
    this.panel.classList.add('is-open');
    this.panel.setAttribute('aria-hidden', 'false');
    this.syncPanelPlacement();
  }

  closePanel() {
    if (!this.panel) return;

    this.cancelPanelSync();
    this.stopPanelDrag();
    this.commentRequestId += 1;
    this.panel.classList.remove('is-open');
    this.panel.setAttribute('aria-hidden', 'true');
    setInert(this.panel, true);
    this.hideError();
    this.currentTargetRow = null;
  }

  readPanelPlacement() {
    if (!this.panel) {
      return {
        top: PANEL_HEADER_HEIGHT + PANEL_MARGIN,
        left: PANEL_MARGIN,
        width: PANEL_WIDTH,
        height: defaultPanelHeight(window.innerHeight)
      };
    }

    const rect = this.panel.getBoundingClientRect();
    const style = window.getComputedStyle(this.panel);
    const width = readNumber(this.panel.style.width, readNumber(style.width, rect.width || PANEL_WIDTH)) || PANEL_WIDTH;
    const height = readNumber(this.panel.style.height, readNumber(style.height, rect.height || defaultPanelHeight(window.innerHeight))) || defaultPanelHeight(window.innerHeight);
    const top = readNumber(this.panel.style.top, readNumber(style.top, rect.top || PANEL_HEADER_HEIGHT + PANEL_MARGIN)) || PANEL_HEADER_HEIGHT + PANEL_MARGIN;
    const left = readNumber(this.panel.style.left, readNumber(style.left, rect.left || window.innerWidth - PANEL_MARGIN - width)) || window.innerWidth - PANEL_MARGIN - width;

    return { top, left, width, height };
  }

  applyPanelPlacement(position) {
    if (!this.panel) return;

    this.panel.style.top = `${Math.round(position.top)}px`;
    this.panel.style.left = `${Math.round(position.left)}px`;
    this.panel.style.right = 'auto';
    this.panel.style.bottom = 'auto';
    this.panel.style.width = `${Math.round(position.width)}px`;
    this.panel.style.height = `${Math.round(position.height)}px`;
    this.panel.style.transform = 'none';
  }

  cancelPanelSync() {
    if (this.panelSyncFrame === null) return;

    cancelFrame(this.panelSyncFrame);
    this.panelSyncFrame = null;
  }

  schedulePanelSync() {
    if (!this.panel || !this.panel.classList.contains('is-open')) return;
    if (this.panelSyncFrame !== null) return;

    this.panelSyncFrame = requestFrame(() => {
      this.panelSyncFrame = null;
      this.syncPanelPlacement();
    });
  }

  syncPanelPlacement() {
    if (!this.panel || !this.panel.classList.contains('is-open')) return;

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const currentPlacement = this.readPanelPlacement();

    if (this.panelPlacementMode === 'manual') {
      this.applyPanelPlacement(clampPanelPosition(currentPlacement, viewportWidth, viewportHeight));
      return;
    }

    const targetRect = this.currentTargetRow ? this.currentTargetRow.getBoundingClientRect() : null;
    const nextPlacement = calculateCommentsPanelPlacement({
      targetRect,
      currentPosition: currentPlacement,
      viewportWidth,
      viewportHeight
    });
    this.applyPanelPlacement(nextPlacement);
  }

  setLoading(loading) {
    if (!this.loading) return;
    this.loading.hidden = !loading;
  }

  showError(message) {
    if (!this.errorBox) return;
    this.errorBox.hidden = false;
    this.errorBox.textContent = message;
  }

  hideError() {
    if (!this.errorBox) return;
    this.errorBox.hidden = true;
    this.errorBox.textContent = '';
  }

  async openComments(button) {
    if (!this.panel || !this.list) return;

    this.currentTarget = {
      targetType: button.dataset.targetType,
      targetId: button.dataset.targetId,
      targetCodeLabel: button.dataset.targetCodeLabel,
      targetCode: button.dataset.targetCode,
      targetName: button.dataset.targetName,
      customerName: button.dataset.customerName,
      selectedTargetMonth: button.dataset.selectedTargetMonth
    };
    this.currentTargetRow = button.closest('tr[data-site-id]');
    this.panelPlacementMode = 'auto';

    this.setPanelMeta(button.dataset);
    this.list.innerHTML = '';
    this.hideError();
    this.setLoading(true);
    this.openPanel();

    const requestId = ++this.commentRequestId;

    try {
      const url = new URL(this.baseCommentsPath, window.location.origin);
      url.searchParams.set('target_type', this.currentTarget.targetType);
      url.searchParams.set('target_id', this.currentTarget.targetId);
      url.searchParams.set('target_month', this.currentTarget.selectedTargetMonth);
      const response = await requestJson(url.toString());
      if (requestId !== this.commentRequestId) return;

      this.renderComments(response.comments || []);
    } catch (error) {
      if (requestId !== this.commentRequestId) return;
      this.showError(error.message || 'コメントの読み込みに失敗しました');
    } finally {
      if (requestId === this.commentRequestId) this.setLoading(false);
    }
  }

  renderComments(comments) {
    if (!this.list || !this.newForm || !this.currentTarget) return;

    this.list.innerHTML = comments.map((comment) => this.commentCardHtml(comment)).join('');
    this.newForm.hidden = !shouldShowNewCommentForm(comments, this.currentTarget.selectedTargetMonth);
    if (this.newTextarea) this.newTextarea.value = '';
    this.syncCommentButtonState(comments.length > 0);
  }

  syncCommentButtonState(active) {
    if (!this.currentTarget) return;

    const button = query(
      `${SELECTORS.commentButton}[data-target-type="${this.currentTarget.targetType}"][data-target-id="${this.currentTarget.targetId}"]`,
      this.root
    );
    if (!button) return;

    button.classList.toggle('blue', active);
    button.classList.toggle('is-active', active);
    const label = active ? 'コメントあり' : 'コメントなし';
    button.setAttribute('title', label);
    button.setAttribute('aria-label', label);
  }

  commentCardHtml(comment) {
    return `
      <article class="beta-comment-card" data-comment-id="${comment.id}">
        <div class="beta-comment-card__month">${escapeHtml(comment.target_month_label)}</div>
        <div class="beta-comment-card__display">
          <div class="beta-comment-card__body">${escapeHtml(comment.comment)}</div>
          <div class="beta-comment-card__meta">入力者: ${escapeHtml(comment.created_by_name)} / 入力日時: ${escapeHtml(comment.created_at_label)}</div>
          <div class="beta-comment-card__meta">更新者: ${escapeHtml(comment.updated_by_name)} / 更新日時: ${escapeHtml(comment.updated_at_label)}</div>
          <div class="beta-comment-card__actions">
            <button type="button" class="ui mini basic button" data-site-cost-comment-edit>編集</button>
            <button type="button" class="ui mini basic button" data-site-cost-comment-history data-history-path="${escapeHtml(comment.history_path)}">履歴</button>
          </div>
        </div>
        <div class="beta-comment-card__edit">
          <textarea class="beta-comment-card__textarea" rows="4" aria-label="コメント編集">${escapeHtml(comment.comment)}</textarea>
          <div class="beta-comment-card__actions">
            <button type="button" class="ui primary mini button" data-site-cost-comment-save>保存</button>
            <button type="button" class="ui mini button" data-site-cost-comment-cancel>キャンセル</button>
          </div>
        </div>
      </article>
    `;
  }

  toggleCardEditing(card, editing) {
    if (!card) return;
    card.classList.toggle('is-editing', editing);
  }

  async createComment(button) {
    if (!this.isConnected || !this.currentTarget || !this.newTextarea) return;

    button.disabled = true;
    this.hideError();

    const requestId = this.commentRequestId;

    try {
      await requestJson(this.baseCommentsPath, {
        method: 'POST',
        body: {
          target_type: this.currentTarget.targetType,
          target_id: this.currentTarget.targetId,
          target_month: this.currentTarget.selectedTargetMonth,
          site_cost_comment: { comment: this.newTextarea.value }
        }
      });

      // Check if still connected and request is not stale
      if (!this.isConnected || requestId !== this.commentRequestId) return;

      await this.reloadCurrentComments();
    } catch (error) {
      // Only show error if still connected and request is not stale
      if (this.isConnected && requestId === this.commentRequestId) {
        this.showError(error.message || 'コメントの保存に失敗しました');
      }
    } finally {
      // Only re-enable button if still connected
      if (this.isConnected) {
        button.disabled = false;
      }
    }
  }

  async updateComment(card, button) {
    if (!card) return;

    const textarea = query('.beta-comment-card__textarea', card);
    const commentId = card.getAttribute('data-comment-id');
    if (!textarea || !commentId) return;

    if (!this.isConnected) return;

    button.disabled = true;
    this.hideError();

    const requestId = this.commentRequestId;

    try {
      await requestJson(`${this.baseCommentsPath}/${commentId}`, {
        method: 'PATCH',
        body: { site_cost_comment: { comment: textarea.value } }
      });

      // Check if still connected and request is not stale
      if (!this.isConnected || requestId !== this.commentRequestId) return;

      await this.reloadCurrentComments();
    } catch (error) {
      // Only show error if still connected and request is not stale
      if (this.isConnected && requestId === this.commentRequestId) {
        this.showError(error.message || 'コメントの更新に失敗しました');
      }
    } finally {
      // Only re-enable button if still connected
      if (this.isConnected) {
        button.disabled = false;
      }
    }
  }

  async reloadCurrentComments() {
    if (!this.isConnected || !this.currentTarget) return;

    const activeButton = query(
      `${SELECTORS.commentButton}[data-target-type="${this.currentTarget.targetType}"][data-target-id="${this.currentTarget.targetId}"]`,
      this.root
    );
    if (!activeButton) return;

    await this.openComments(activeButton);
  }

  async openHistory(historyPath) {
    const $ = getJQuery();
    if (!this.historyModal || !$ || typeof $.fn?.modal !== 'function' || !historyPath) return;

    this.historyLoading.hidden = false;
    this.historyList.innerHTML = '';
    $(this.historyModal).modal('show');

    const requestId = ++this.historyRequestId;

    try {
      const response = await requestJson(new URL(historyPath, window.location.origin).toString());
      if (requestId !== this.historyRequestId) return;

      this.historyList.innerHTML = buildHistoryTableHtml(response.histories || []);
    } catch (error) {
      if (requestId !== this.historyRequestId) return;
      this.historyList.innerHTML = `<div class="ui negative message">${escapeHtml(error.message || '履歴の読み込みに失敗しました')}</div>`;
    } finally {
      if (requestId === this.historyRequestId) this.historyLoading.hidden = true;
    }
  }
}

const initializeSiteCostsBetaPage = () => {
  const root = query(SELECTORS.root);
  if (!root) return;

  if (activePage && activePage.root === root && activePage.isConnected) return;

  if (activePage && activePage.root !== root) {
    activePage.disconnect();
    activePage = null;
  }

  if (!activePage) {
    activePage = new SiteCostsBetaPage(root);
  }

  activePage.connect();
};

const handleBeforeCache = () => {
  if (!activePage) return;

  activePage.disconnect();
  activePage = null;
};

const installLifecycleListeners = () => {
  if (lifecycleListenersInstalled) return;

  lifecycleListenersInstalled = true;
  LOAD_EVENT_NAMES.forEach((eventName) => {
    document.addEventListener(eventName, initializeSiteCostsBetaPage);
  });
  ['turbolinks:before-cache', 'turbo:before-cache'].forEach((eventName) => {
    document.addEventListener(eventName, handleBeforeCache);
  });
};

installLifecycleListeners();
initializeSiteCostsBetaPage();
